import { Component, OnInit, Input, OnDestroy, AfterViewInit, ElementRef, ViewChild, OnChanges, SimpleChanges, Renderer2 } from '@angular/core';

// interface Banner {
//   id: number;
//   slug: string;
//   title: string;
//   category: string;
//   description: string;
//   attachment: string;
// }

@Component({
  selector: 'app-slider',
  templateUrl: './app-slider.component.html',
  styleUrls: ['./app-slider.component.scss'],
  standalone: false,
})
export class AppSliderComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
  @Input() allBanners: any;
  // @Input() category: string;
  @Input() slideSpeedMs: number = 500;
  @Input() autoplayIntervalMs: number = 3000;
  @Input() isRTL: boolean = false;
  @Input() loop: boolean = true;
  @Input() isSquare: boolean = true;
  sizeVar: string = 'squareTrue';
  @ViewChild('slidesWrapper', { static: true }) slidesWrapper: ElementRef;

  displayedBanners: any;
  currentSlideIndex: number = 0;
  private autoplayTimer: any;
  private slideWidth: number = 0;

  // Touch/Swipe properties
  private startX: number = 0;
  private currentX: number = 0;
  private isSwiping: boolean = false;
  private deltaX: number = 0; // Distance moved during swipe

  constructor(private el: ElementRef, private renderer: Renderer2) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['allBanners'] || changes['category']) {
      this.filterBanners();
      this.currentSlideIndex = 0; // Reset to first slide
      this.updateSlidePosition(true); // Update immediately without transition
      this.startAutoplay();
    }
    if (changes['loop']) {
      this.startAutoplay();
    }
    if (changes['slideSpeedMs']) {
      // Update CSS variable if slideSpeedMs changes
      this.renderer.setStyle(this.slidesWrapper.nativeElement, '--slide-speed', `${this.slideSpeedMs}ms`);
    }
  }

  ngOnInit() {
    this.filterBanners();
    this.startAutoplay();
    if (this.isSquare == true) {
      this.sizeVar = 'squareTrue';
    } else {
      this.sizeVar = 'squareFalse';
    }
  }

  ngAfterViewInit() {
    // Set CSS variable for transition speed initially
    this.renderer.setStyle(this.slidesWrapper.nativeElement, '--slide-speed', `${this.slideSpeedMs}ms`);

    // Use a small timeout or IntersectionObserver if slides are dynamically loaded
    // Using ResizeObserver for more robust width calculation on resize
    setTimeout(() => {
      this.calculateSlideWidth();
      this.updateSlidePosition(); // Set initial position
    }, 0); // Ensures initial render is complete

    // Listen for resize events to recalculate slide width
    if (typeof ResizeObserver !== 'undefined') {
      const resizeObserver = new ResizeObserver(() => {
        this.calculateSlideWidth();
        this.updateSlidePosition();
      });
      resizeObserver.observe(this.el.nativeElement);
      // Clean up observer on destroy
      this.ngOnDestroy = () => {
        resizeObserver.disconnect();
        this.stopAutoplay();
      };
    } else {
      // Fallback for older browsers (though less accurate)
      window.addEventListener('resize', this.onWindowResize.bind(this));
      this.ngOnDestroy = () => {
        window.removeEventListener('resize', this.onWindowResize.bind(this));
        this.stopAutoplay();
      };
    }
  }

  private onWindowResize() {
    this.calculateSlideWidth();
    this.updateSlidePosition();
  }

  // Calculate slide width robustly
  private calculateSlideWidth() {
    // Get the width of the main container (slider-container)
    const container = this.el.nativeElement.querySelector('.slider-container');
    if (container) {
      this.slideWidth = container.clientWidth;
      // } else {
      // Fallback if container not found, try getting from first slider-item
      // const slideItem = this.slidesWrapper.nativeElement.querySelector('.slider-item');
      // if (slideItem) {
      //   this.slideWidth = slideItem.clientWidth;
    }
    // }
  }

  ngOnDestroy() {
    this.stopAutoplay();
    // Clean up any resize event listener if not using ResizeObserver
  }

  filterBanners() {
    // if (this.allBanners) {
    //   this.displayedBanners = this.allBanners || [];
    // } else {
    //   this.displayedBanners = this.allBanners || [];
    // }
    this.displayedBanners = this.allBanners;

    // Ensure currentSlideIndex is valid after filtering
    if (this.currentSlideIndex >= this.displayedBanners.length && this.displayedBanners.length > 0) {
      this.currentSlideIndex = this.displayedBanners.length - 1;
    } else if (this.displayedBanners.length === 0) {
      this.currentSlideIndex = 0;
    }
  }

  startAutoplay() {
    this.stopAutoplay();
    if (this.loop && this.autoplayIntervalMs > 0 && this.displayedBanners.length > 1) {
      this.autoplayTimer = setInterval(() => {
        this.nextSlide();
      }, this.autoplayIntervalMs);
    }
  }

  stopAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  goToSlide(index: number, preventAutoplayReset: boolean = false) {
    if (!this.displayedBanners || this.displayedBanners.length === 0) return;

    if (this.loop) {
      if (index < 0) {
        this.currentSlideIndex = this.displayedBanners.length - 1;
      } else if (index >= this.displayedBanners.length) {
        this.currentSlideIndex = 0;
      } else {
        this.currentSlideIndex = index;
      }
    } else {
      if (index < 0 || index >= this.displayedBanners.length) {
        return;
      }
      this.currentSlideIndex = index;
    }

    this.updateSlidePosition();
    if (!preventAutoplayReset) {
      this.startAutoplay();
    }
  }

  nextSlide() {
    this.goToSlide(this.currentSlideIndex + 1);
  }

  prevSlide() {
    this.goToSlide(this.currentSlideIndex - 1);
  }

  private updateSlidePosition(instant: boolean = false) {
    if (!this.slidesWrapper || this.slideWidth === 0) return;

    let translateXValue = -this.currentSlideIndex * this.slideWidth;

    if (this.isRTL) {
      translateXValue = translateXValue * -1;
    }

    // Apply swipe delta if actively swiping
    if (this.isSwiping) {
      translateXValue += this.deltaX;
      // Disable transition temporarily during swipe for smooth dragging
      this.renderer.setStyle(this.slidesWrapper.nativeElement, 'transition', 'none');
    } else {
      // Re-enable transition after swipe or for regular navigation
      this.renderer.setStyle(this.slidesWrapper.nativeElement, 'transition', `transform ${this.slideSpeedMs}ms ease-in-out`);
      if (instant) {
        // For immediate updates without transition (e.g., on init/filter)
        this.renderer.setStyle(this.slidesWrapper.nativeElement, 'transition', 'none');
      }
    }

    this.renderer.setStyle(this.slidesWrapper.nativeElement, 'transform', `translateX(${translateXValue}px)`);
  }

  // --- Touch/Swipe Handlers ---
  handleTouchStart(event: TouchEvent) {
    if (this.displayedBanners.length <= 1) return; // No need to swipe if 0 or 1 slide

    this.stopAutoplay();
    this.startX = event.touches[0].clientX;
    this.isSwiping = true;
    this.renderer.setStyle(this.slidesWrapper.nativeElement, 'transition', 'none'); // Disable transition during drag
  }

  handleTouchMove(event: TouchEvent) {
    if (!this.isSwiping) return;

    this.currentX = event.touches[0].clientX;
    this.deltaX = this.currentX - this.startX;

    // Apply the current drag position
    let transform = -this.currentSlideIndex * this.slideWidth + this.deltaX;
    if (this.isRTL) {
      transform = -transform; // Reverse direction for RTL
    }
    this.renderer.setStyle(this.slidesWrapper.nativeElement, 'transform', `translateX(${transform}px)`);
  }

  handleTouchEnd() {
    if (!this.isSwiping) return;

    this.isSwiping = false;
    this.renderer.removeStyle(this.slidesWrapper.nativeElement, 'transition'); // Re-enable transition for snapping

    const swipeThreshold = this.slideWidth * 0.2; // 20% of slide width
    const currentSlideTarget = this.currentSlideIndex;

    if (this.deltaX > swipeThreshold) {
      // Swiped right (to previous slide)
      this.prevSlide();
    } else if (this.deltaX < -swipeThreshold) {
      // Swiped left (to next slide)
      this.nextSlide();
    } else {
      // Not enough swipe, snap back to current slide
      this.goToSlide(currentSlideTarget, true); // Don't reset autoplay on snap-back
    }

    this.deltaX = 0; // Reset delta
    this.startAutoplay(); // Restart autoplay after gesture
  }
}
